import { sanitizeUsernameForOperationName } from '../utils/username';
import {
	EDGE_CASE_TYPE_SELECTIONS,
	FRAGMENT_FIELD_DENY_SET,
	type EdgeCaseTypeSelectionConfig,
} from './GraphQLFragmentGenerator.constants';
import type { FieldDef, TypeEntry, TypeRef, TypesByName } from './GraphQLSchemaAtoms';
import { type GraphQLSchemaVariant } from './GraphQLSchemaAtoms';

export type GenerateFragmentsOptions = {
	depth?: number;
	includeTypename?: boolean;
	skipDeprecated?: boolean;
	allowedTypes?: string[];
	skipExperimental?: boolean;

	// for fields that are objects, auto-select the id field if it exists, otherwise omit it entirely.
	autoSelectIdForObjects?: boolean;
	// Prefix for the query name (default: TwgLabs)
	queryNamePrefix?: string;
	// Suffix appended after the username (e.g. '_CyphieDefault')
	queryNameSuffix?: string;
	// Which schema variant to target (default: internal)
	schemaVariant?: GraphQLSchemaVariant;
};

// Gets the base (named) type for a field
function unwrapNamed(type: TypeRef): { kind: 'NAMED'; name: string } | null {
	if (!type) {
		return null;
	}
	if ((type as any).kind === 'NAMED') {
		return type as any;
	}
	if ((type as any).ofType) {
		return unwrapNamed((type as any).ofType);
	}
	return null;
}

// Checks if a type is a simple value (like number/string) or a fixed list of values (enum)
function isScalarLike(entry: TypeEntry | undefined): boolean {
	if (!entry) {
		return false;
	}
	return entry.kind === 'SCALAR' || entry.kind === 'ENUM';
}

// Finds a type by name
function getTypeEntry(typesByName: TypesByName, name?: string | null): TypeEntry | undefined {
	if (!name) {
		return undefined;
	}
	return typesByName[name.toLowerCase()];
}

// Detects fields marked as experimental in their description and skips them when specified
function isExperimentalField(f: FieldDef | undefined): boolean {
	const d = (f?.description || '').toLowerCase();
	return (
		d.includes('experimental') ||
		d.includes('not intended for general use') ||
		d.includes('clients should use it with care') ||
		d.includes('before using this') ||
		d.includes('before using this query')
	);
}

/** Chooses which fields to include for a type, following the rules and depth setting. */
function pickFields(
	entry: TypeEntry,
	typesByName: TypesByName,
	opts: Required<GenerateFragmentsOptions>,
	depth: number,
	seen: Set<string>,
	typeNamePrefix?: string,
): string[] {
	if (!entry.fields || entry.fields.length === 0) {
		return [];
	}

	const scalarSelections: string[] = [];
	const objectSelections: string[] = [];
	const deepSelections: string[] = [];
	const idFieldCache = new Map<string, boolean>();

	const hasScalarId = (name: string | undefined) => {
		const key = (name || '').toLowerCase();
		if (!key) {
			return false;
		}

		const cached = idFieldCache.get(key);
		if (cached != null) {
			return cached;
		}

		const e = getTypeEntry(typesByName, name || '');
		const found = !!e?.fields?.some(
			(fld) =>
				fld?.name === 'id' &&
				isScalarLike(getTypeEntry(typesByName, unwrapNamed(fld?.type as any)?.name)),
		);
		idFieldCache.set(key, found);

		return found;
	};

	for (const f of entry.fields) {
		if (!f || !f.name) {
			continue;
		}
		if (opts.skipDeprecated && f.isDeprecated) {
			continue;
		}

		if (opts.skipExperimental && isExperimentalField(f)) {
			continue;
		}

		// Skip fields in the deny list (e.g., to avoid hydration issues)
		if (FRAGMENT_FIELD_DENY_SET.has(f.name)) {
			continue;
		}

		// Skip fields that require arguments
		if (Array.isArray(f.args) && f.args.length > 0) {
			continue;
		}

		const named = f.type ? unwrapNamed(f.type) : null;
		if (!named) {
			continue;
		}
		const child = getTypeEntry(typesByName, named.name);
		if (!child) {
			continue;
		}

		// Apply type prefix for aliasing if provided
		const fieldName = typeNamePrefix ? `${typeNamePrefix}_${f.name}` : f.name;
		const fieldAlias = typeNamePrefix ? `${fieldName}: ${f.name}` : f.name;

		// Apply any edge case rules for the field's underlying type
		const edgeCaseSelection = EdgeCaseRules({
			fieldTypeName: child.name,
			fieldAlias,
		});
		if (edgeCaseSelection) {
			objectSelections.push(edgeCaseSelection);
			continue;
		}

		if (isScalarLike(child)) {
			scalarSelections.push(fieldAlias);
		} else if (opts.autoSelectIdForObjects && child.kind === 'OBJECT') {
			if (hasScalarId(child.name)) {
				objectSelections.push(`${fieldAlias} {\n${indent('id')}\n}`);
			}
		} else if (
			depth > 0 &&
			(child.kind === 'OBJECT' || child.kind === 'INTERFACE' || child.kind === 'UNION')
		) {
			// Prevent infinite recursion on self-referential fields
			const key = `${entry.name}::${f.name}`.toLowerCase();
			if (seen.has(key)) {
				continue;
			}
			seen.add(key);

			const subSel = renderSelectionForType(child, typesByName, opts, depth - 1, seen);
			if (subSel) {
				deepSelections.push(`${fieldAlias} {\n${indent(subSel)}\n}`);
			}
		} else {
			// skip unknown or input objects
		}
	}

	return [...scalarSelections, ...objectSelections, ...deepSelections];
}

// Adds left padding to each line for nicer formatting
function indent(str: string, spaces = 2): string {
	const pad = ' '.repeat(spaces);
	return str
		.split('\n')
		.map((l) => (l.length ? pad + l : l))
		.join('\n');
}

type EdgeCaseRuleContext = {
	fieldTypeName: string;
	fieldAlias: string;
};

/**
 * Applies any bespoke selection rules for specific field types.
 * This is used for edge cases where we always want to include certain
 * subfields (e.g. ConfluencePageLinks) regardless of depth or options.
 */
function EdgeCaseRules(ctx: EdgeCaseRuleContext): string | null {
	const typeConfig: EdgeCaseTypeSelectionConfig | undefined =
		EDGE_CASE_TYPE_SELECTIONS[ctx.fieldTypeName];
	if (!typeConfig) {
		return null;
	}

	return `${ctx.fieldAlias} {\n${indent(typeConfig.selection)}\n}`;
}

// Serializes a type reference including nullability for comparison
function serializeTypeRef(typeRef: TypeRef | null | undefined): string {
	if (!typeRef) {
		return 'null';
	}

	const ref = typeRef as any;

	if (ref.kind === 'NON_NULL') {
		return `${serializeTypeRef(ref.ofType)}!`;
	}

	if (ref.kind === 'LIST') {
		return `[${serializeTypeRef(ref.ofType)}]`;
	}

	if (ref.kind === 'NAMED') {
		return ref.name;
	}

	return 'unknown';
}

// Detects if a field name has conflicting types or nullability across multiple types
function detectFieldConflicts(
	possibleTypes: string[],
	typesByName: TypesByName,
): Map<string, Set<string>> {
	const fieldTypeMap = new Map<string, Set<string>>();

	for (const typeName of possibleTypes) {
		const typeEntry = getTypeEntry(typesByName, typeName);
		if (!typeEntry?.fields) {
			continue;
		}

		for (const field of typeEntry.fields) {
			if (!field?.name) {
				continue;
			}

			// Serialize the full type signature including nullability
			const typeSignature = serializeTypeRef(field.type);

			if (!fieldTypeMap.has(field.name)) {
				fieldTypeMap.set(field.name, new Set());
			}
			fieldTypeMap.get(field.name)!.add(typeSignature);
		}
	}

	// Return only fields that have conflicting type signatures (including nullability)
	const conflicts = new Map<string, Set<string>>();
	for (const [fieldName, typeSignatures] of fieldTypeMap.entries()) {
		if (typeSignatures.size > 1) {
			conflicts.set(fieldName, typeSignatures);
			console.debug(
				`[detectFieldConflicts] Field "${fieldName}" has conflicting signatures:`,
				Array.from(typeSignatures),
			);
		}
	}

	return conflicts;
}

// Builds the list of fields to fetch for a type (including interface/union shapes)
function renderSelectionForType(
	entry: TypeEntry,
	typesByName: TypesByName,
	opts: Required<GenerateFragmentsOptions>,
	depth: number,
	seen: Set<string>,
	filterPossibleTypes = false,
): string {
	const parts: string[] = [];
	if (opts.includeTypename) {
		parts.push('__typename');
	}

	if (entry.kind === 'OBJECT') {
		parts.push(...pickFields(entry, typesByName, opts, depth, new Set(seen)));
		return parts.join('\n');
	}

	if (entry.kind === 'INTERFACE' || entry.kind === 'UNION') {
		let poss = entry.possibleTypes || [];
		if (filterPossibleTypes && opts.allowedTypes.length > 0) {
			const allow = new Set(opts.allowedTypes.map((n) => n.toLowerCase()));
			poss = poss.filter((p) => allow.has(p.toLowerCase()));
		}
		if (poss.length === 0) {
			return parts.join('\n');
		}

		// Detect field conflicts across all possible types
		const conflicts = detectFieldConflicts(poss, typesByName);

		for (const p of poss) {
			const pt = getTypeEntry(typesByName, p);
			if (!pt) {
				continue;
			}

			// Pass the type name as prefix only if this type has conflicting fields
			// This will apply aliases automatically to all fields (including nested ones)
			const hasConflicts = pt.fields?.some((f) => f?.name && conflicts.has(f.name));
			const typePrefix = hasConflicts ? pt.name : undefined;

			const sel = pickFields(pt, typesByName, opts, depth, new Set(seen), typePrefix);

			const inner = [opts.includeTypename ? '__typename' : null, ...sel].filter(Boolean).join('\n');
			parts.push(`... on ${pt.name} {\n${indent(inner)}\n}`);
		}
		return parts.join('\n');
	}

	// Scalars/enums do not have selections
	return parts.join('\n');
}

// Creates a reusable fragment called 'DataFields' for the chosen type
export function generateFragmentForRootType(
	rootTypeName: string,
	typesByName: TypesByName,
	options?: GenerateFragmentsOptions,
): { fragment: string; warnings: string[] } {
	const opts: Required<GenerateFragmentsOptions> = {
		depth: options?.depth ?? 2,
		includeTypename: options?.includeTypename ?? true,
		skipDeprecated: options?.skipDeprecated ?? true,
		allowedTypes: options?.allowedTypes ?? [],
		skipExperimental: options?.skipExperimental ?? true,
		autoSelectIdForObjects: options?.autoSelectIdForObjects ?? false,
		queryNamePrefix: options?.queryNamePrefix ?? 'TwgLabs',
		queryNameSuffix: options?.queryNameSuffix ?? '',
		schemaVariant: options?.schemaVariant ?? 'internal',
	};

	const entry = getTypeEntry(typesByName, rootTypeName);
	if (!entry) {
		return {
			fragment: `# Unknown type ${rootTypeName}`,
			warnings: [`Type not found: ${rootTypeName}`],
		};
	}

	const body = renderSelectionForType(entry, typesByName, opts, opts.depth, new Set(), true);
	const fragment = `fragment DataFields on ${entry.name} {\n${indent(body)}\n}`;
	return { fragment, warnings: [] };
}

// Builds the full query string for the External (Query Builder) schema
function buildExternalSchemaQuery(fragment: string, opName: string, limit: number): string {
	return `${fragment}

query ${opName}($cypherQuery: String!, $params: CypherRequestParams, $after: String) {
  cypherQuery(query: $cypherQuery, params: $params, first: ${limit}, after: $after) {
    edges {
      node {
        columns {
          key
          value {
            ... on CypherQueryResultNode {
              id
              data {
                ...DataFields
              }
            }
            ... on CypherQueryResultListNode {
              nodes {
                id
                data {
                  ...DataFields
                }
              }
            }
          }
        }
      }
    }
  }
}`;
}

// Builds the full query string for the Internal (Labs) schema
function buildInternalSchemaQuery(fragment: string, opName: string, limit: number): string {
	return `${fragment}

query ${opName}($cypherQuery: String!, $params: JSON, $version: GraphStoreCypherQueryV2VersionEnum) {
  graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
    cypherQueryV2(
      query: $cypherQuery
      params: $params
      version: $version
      first: ${limit}
    ) {
      edges {
        node {
          __typename
          columns {
            key
            value {
              ... on GraphStoreCypherQueryV2AriNode {
                id
                data {
                  ...DataFields
                }
              }
              ... on GraphStoreCypherQueryV2NodeList {
                nodes {
                  id
                  data {
                    ...DataFields
                  }
                }
              }
              ... on GraphStoreCypherQueryV2StringObject {
                str_val: value
              }
              ... on GraphStoreCypherQueryV2StringListObject {
                str_vals: values
              }
              ... on GraphStoreCypherQueryV2IntObject {
                int_val: value
              }
              ... on GraphStoreCypherQueryV2IntListObject {
                int_vals: values
              }
              ... on GraphStoreCypherQueryV2TimestampObject {
                timestamp: value
              }
              ... on GraphStoreCypherQueryV2TimestampListObject {
                timestamps: values
              }
              ... on GraphStoreCypherQueryV2BooleanObject {
                bool_val: value
              }
            }
          }
        }
      }
    }
  }
}`;
}

// Creates the full query and attaches the 'DataFields' fragment to it (using single query API)
export function generateCypherQuery(
	rootTypeName: string,
	typesByName: TypesByName,
	options: GenerateFragmentsOptions | undefined,
	limit: number,
	username?: string,
): { query: string; fragment: string; warnings: string[] } {
	const { fragment, warnings } = generateFragmentForRootType(rootTypeName, typesByName, options);

	const prefix = options?.queryNamePrefix ?? 'TwgLabs';
	const suffix = options?.queryNameSuffix ?? '';
	const opName = username
		? `${prefix}_${sanitizeUsernameForOperationName(username)}${suffix}`
		: `${prefix}${suffix}`;

	const query =
		options?.schemaVariant === 'external'
			? buildExternalSchemaQuery(fragment, opName, limit)
			: buildInternalSchemaQuery(fragment, opName, limit);

	return { query, fragment, warnings };
}

// Returns the default root union type for the given schema variant
function getDefaultRootTypeName(variant: GraphQLSchemaVariant): string {
	return variant === 'external' ? 'CypherQueryResultData' : 'GraphStoreCypherQueryV2AriNodeUnion';
}

// Creates a fragment but only for the specific concrete types listed
export function generateFragmentFromConcreteTypes(
	concreteTypeNames: string[],
	typesByName: TypesByName,
	options?: GenerateFragmentsOptions & { rootTypeName?: string },
): { fragment: string; warnings: string[] } {
	const root =
		options?.rootTypeName ?? getDefaultRootTypeName(options?.schemaVariant ?? 'internal');
	return generateFragmentForRootType(root, typesByName, {
		...options,
		allowedTypes: concreteTypeNames,
	});
}

// Creates the full query for just the types listed, using a username in the query name when provided (single query API)
export function generateCypherQueryFromConcreteTypes(
	concreteTypeNames: string[],
	typesByName: TypesByName,
	options: (GenerateFragmentsOptions & { rootTypeName?: string }) | undefined,
	limit: number,
	username?: string,
): { query: string; fragment: string; warnings: string[] } {
	const root =
		options?.rootTypeName ?? getDefaultRootTypeName(options?.schemaVariant ?? 'internal');
	return generateCypherQuery(
		root,
		typesByName,
		{
			...options,
			allowedTypes: concreteTypeNames,
		},
		limit,
		username,
	);
}
